import {
  IInParamsValues,
  TClearOptions,
  TControlName,
} from '@infotech-soe/metaschema';
import { createSelector } from '@reduxjs/toolkit';

import { ClientContext } from '../../services/context/IContextService';
import { FormTitle } from '../../services/form/FormTitle';
import {
  DictionaryInputFormControlViewModel,
  FormControlViewModel,
  FormControlViewModelType,
  TabsFormControlChildAuxProps,
  TabsFormControlViewModel,
} from '../../services/form/view-model/FormControlViewModel';
import { PredefinedForms } from '../../services/form/view-model/FormCreateViewModel';
import { FormSourceType } from '../../services/form/view-model/FormSource';
import {
  FormControls,
  FormViewModel,
} from '../../services/form/view-model/FormViewModel';
import UniqueId from '../../services/uid/UniqueId';
import { AppState } from '../reducers/root';
import { FormEntity, FormParent } from '../slices/form';

import { getDatasetState } from './dataset';
import { hasPendingFormUpdates } from './operationProgress';

const getFormEntity = (state: AppState, id: UniqueId): FormEntity =>
  state.form.forms[id];

export const getFormContext = (state: AppState, id: UniqueId): ClientContext =>
  getFormEntity(state, id).context;

export const getFormState = (state: AppState, id: UniqueId): FormViewModel =>
  getFormEntity(state, id).form;

export const getFormParent = (
  state: AppState,
  id: UniqueId
): FormParent | undefined => getFormEntity(state, id).parent;

export const getFormCommandName = (
  state: AppState,
  id: UniqueId
): FormEntity['command'] => getFormEntity(state, id).command;

export const getFormControlNames = (
  state: AppState,
  id: UniqueId
): TControlName[] => getFormState(state, id).controlsOrder;

export const getFormControls = (state: AppState, id: UniqueId): FormControls =>
  getFormState(state, id).controls;

export const getFormControlByName = (
  state: AppState,
  id: UniqueId,
  name: TControlName
): FormControlViewModel => getFormControls(state, id)[name];

export const getRootFormControlId = (
  state: AppState,
  formId: UniqueId
): UniqueId => getFormState(state, formId).rootControl;

export const getFormControlForbidden = createSelector(
  [
    getFormControlByName,
    (state: AppState) => state,
    (_state: AppState, formId: UniqueId) => formId,
  ],
  (control, state, formId): boolean => {
    if ('children' in control) {
      return control.children.some(child =>
        getFormControlForbidden(state, formId, child)
      );
    } else {
      return (
        !!control.invalid ||
        (control.hasValue &&
          !!control.required &&
          (control.value === null ||
            (typeof control.value === 'string' &&
              control.value.trim().length === 0)))
      );
    }
  }
);

export const getTabsControlChildrenProps = createSelector(
  [
    (state: AppState) => state,
    (_state: AppState, formId: UniqueId) => formId,
    (_state: AppState, _formId: UniqueId, controlId: UniqueId) => controlId,
  ],
  (state, formId, controlId): Record<string, TabsFormControlChildAuxProps> => {
    const auxProps: Record<string, TabsFormControlChildAuxProps> = {};
    const control = getFormControlByName(
      state,
      formId,
      controlId
    ) as TabsFormControlViewModel;

    for (const tab of control.children) {
      const ctrl = getFormControlByName(state, formId, tab);
      auxProps[tab] = {
        label: ctrl !== undefined ? ctrl.label : '',
        hidden: ctrl?.visible !== undefined && ctrl.visible === false,
        warning:
          ctrl !== undefined && getFormControlForbidden(state, formId, tab),
      };
    }

    return auxProps;
  }
);

export const submitForbidden = createSelector(
  [
    (state: AppState) => state,
    (_state: AppState, formId: UniqueId) => formId,
    getRootFormControlId,
  ],
  getFormControlForbidden
);

export const getActiveFormsIds = (state: AppState): UniqueId[] =>
  state.form.order;

export const getLastActiveFormId = (state: AppState): UniqueId | undefined => {
  const formIds = getActiveFormsIds(state);
  return formIds.at(-1);
};

export const getFormSourceType = (
  state: AppState,
  id: UniqueId
): FormSourceType | PredefinedForms => {
  const { source } = getFormState(state, id);
  if (source.type === FormSourceType.PREDEFINED) {
    return source.action;
  }
  return source.type;
};

export const getFormTitle = (state: AppState, id: UniqueId): FormTitle =>
  getFormState(state, id).title;

export const getFormWidth = (
  state: AppState,
  id: UniqueId
): number | string | undefined => getFormState(state, id).width;

export const getFormHeight = (
  state: AppState,
  id: UniqueId
): number | string | undefined => getFormState(state, id).height;

export const isFormReadonly = (state: AppState, id: UniqueId): boolean =>
  getFormState(state, id).readonly;

export const getFormClear = (
  state: AppState,
  id: UniqueId
): TClearOptions | void => getFormState(state, id).clear;

export const isSwitchDisabled = (
  state: AppState,
  id: UniqueId
): Record<'next' | 'prev', boolean> => {
  const res = { next: false, prev: false };
  const { source } = getFormState(state, id);
  if (
    source.type === FormSourceType.PREDEFINED &&
    source.action === PredefinedForms.REVIEW
  ) {
    const dataset = getDatasetState(state, source.dataset);
    if (dataset.activeRowId === dataset.rows[0][dataset.rowIdField]) {
      res.prev = true;
    }
    if (
      dataset.activeRowId ===
      dataset.rows[dataset.rows.length - 1][dataset.rowIdField]
    ) {
      res.next = true;
    }
  }

  return res;
};

const PARAMS_PROCESSORS: Record<
  'control' | 'value',
  (controls: FormControls, inParam: any) => any
> = {
  control: (controls: FormControls, inParam: any) => {
    const ctrl = controls[inParam.val];
    return ctrl.hasValue ? ctrl.value : undefined;
  },
  value: (_controls: FormControls, inParam: any) => inParam.val,
};

export const getDictionaryFormControlInParams = createSelector(
  [
    getFormControls,
    (_state: AppState, _id: UniqueId, name: TControlName) => name,
  ],
  (controls, name): IInParamsValues => {
    const control = controls[name] as DictionaryInputFormControlViewModel;

    const inParams: IInParamsValues = {};
    for (const [key, definition] of Object.entries(control.inParams || {})) {
      const value = PARAMS_PROCESSORS[definition.kind](controls, definition);
      if (value !== undefined) {
        inParams[key] = value;
      }
    }

    return inParams;
  }
);

export const getFormRelatedTree = (
  state: AppState,
  formId: UniqueId
): UniqueId => {
  const { source } = getFormState(state, formId);

  if (source.type === FormSourceType.PREDEFINED && source.treeId) {
    return source.treeId;
  }

  return '';
};

export const getFormRelatedCategory = (
  state: AppState,
  formId: UniqueId
): UniqueId => getFormState(state, formId).source.category;

export const getControlValue = (
  state: AppState,
  id: UniqueId,
  control: TControlName
): any => {
  const stateControl = getFormControlByName(state, id, control);
  return 'value' in stateControl ? stateControl.value : undefined;
};

export const isSubmitting = (state: AppState, formId: UniqueId): boolean =>
  getFormState(state, formId).submitting &&
  !hasPendingFormUpdates(state, formId);

export const getHasHints = (state: AppState, formId: UniqueId): boolean =>
  getFormState(state, formId).hasHints;

export const getAreHintsShown = (state: AppState, formId: UniqueId): boolean =>
  getFormState(state, formId).areHintsShown;

export const getFormButtons = createSelector(getFormControls, controls =>
  Object.values(controls).filter(
    control => control.type === FormControlViewModelType.FORM_BUTTON
  )
);

export const getReviewForm = createSelector(
  [
    (state: AppState) => state.form.forms,
    (_state: AppState, datasetId: UniqueId) => datasetId,
  ],
  (forms, datasetId): FormEntity | undefined =>
    Object.values(forms).find(
      ({ form: { source } }: FormEntity) =>
        source.type === FormSourceType.PREDEFINED &&
        source.action === PredefinedForms.REVIEW &&
        source.dataset === datasetId
    )
);
